import type { ReactNode } from "react";

import clsx from "clsx";

import type { TypoStyle } from "../../../Typo";

import { Typo } from "../../../Typo";

type MobileFieldRowProps = {
  label: string;
  value: ReactNode;
  info?: ReactNode;
  /**
   * Value scale — defaults to `labelSM` (Jakarta) for the mobile-card token
   * rule (PART-4052 handoff spec §3). The program detail sheet passes
   * `bodySM` instead: its KV/Performance rows are plain Roboto per the
   * canvas's `SmPdLine` helper and PART-3932 spec §3 ("Do not use
   * Jakarta-medium for any value" in the drawer) — only section titles and
   * the HOID identity are Jakarta there.
   */
  valueType?: TypoStyle;
  /** Compact design scale (program list card): `bodyXS` label, `labelXS` value, and a value box that does not inherit the page's 24px line-height. */
  isCompact?: boolean;
  /**
   * Escape hatch for the row's own vertical padding — the default `py-2`
   * gives adjacent rows a 16px gap (used by the detail sheet's KV/Performance
   * sections). `MobileProgramCard` overrides it to `!py-0` and puts the 8px
   * item-to-item gap on its own container instead, since that gap is
   * specific to the program list card, not this shared row.
   */
  className?: string;
};

/**
 * One label→value row for a mobile card/sheet. Label is always
 * `typo-body-sm`/`--text-secondary`; the value scale differs by context
 * (see `valueType`). Shared between `MobileProgramCard` and
 * `MobileProgramDetailSheet` rather than duplicated.
 */
export const MobileFieldRow = ({
  label,
  value,
  info,
  valueType,
  isCompact = false,
  className,
}: MobileFieldRowProps) => (
  <div
    className={clsx("flex items-center justify-between gap-2 py-2", className)}
  >
    {/* min-w-0 lets the label truncate instead of forcing the row wider —
        needed for long Sub IB partner names in the split table (handoff
        spec §4.6: "long partner names truncated with ellipsis"). */}
    <span className="flex min-w-0 items-center gap-1">
      <Typo
        element="span"
        type={isCompact ? "bodyXS" : "bodySM"}
        textColor="text-text-secondary"
        className="truncate"
      >
        {label}
      </Typo>
      {info}
    </span>
    <span className={clsx("shrink-0 text-right", isCompact && "flex")}>
      {typeof value === "string" || typeof value === "number" ? (
        <Typo
          element="span"
          type={valueType ?? (isCompact ? "labelXS" : "labelSM")}
          textColor="text-text-primary"
        >
          {value}
        </Typo>
      ) : (
        value
      )}
    </span>
  </div>
);
